import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/LocaleContext";
import HammLogoA from "./HammLogoA";

const IMAGES = [
  { img: "/assets/konstruktion.jpg", pos: "", altSuffix: "3D-Konstruktion in SOLIDWORKS für Blechbaugruppen bei Hamm EP in Geldern" },
  { img: "/assets/hero-laser.jpg", pos: "object-center", altSuffix: "6-kW-Faserlaser-Schneidkopf beim Trennen von Edelstahl-Blech in Geldern" },
  { img: "/assets/abkanten.jpg", pos: "", altSuffix: "CNC-Abkantpresse mit 2500 mm Kantlänge – Blechabkanten in NRW" },
  { img: "/assets/schweissen-wig.jpg", pos: "object-[50%_35%]", altSuffix: "WIG-Schweißbaugruppe aus Edelstahl – Hamm EP GmbH, Bahnindustrie-Zulieferer" },
];

export default function Services() {
  const { t, dict, locale } = useT();
  const items = dict.services.items;

  return (
    <section id="leistungen" className="py-32 lg:py-44 bg-[#F7F9FA]" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-20 lg:mb-24"
        >
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[var(--ci-blue)] mb-6">{t("services.eyebrow")}</p>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl text-[var(--ci-grey)]">
            {t("services.title1")}<span className="text-[var(--ci-blue)]">{t("services.title2")}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((s, i) => {
            const media = IMAGES[i] || {};
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: (i % 2) * 0.12 }}
                className="group bg-white border border-[#E3E8EC] rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-[var(--ci-turquoise)]/40 hover:shadow-[0_20px_60px_rgba(0,91,234,0.08)] transition-all duration-300 flex flex-col"
                data-testid={`service-card-${String(i + 1).padStart(2, "0")}`}
              >
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img src={media.img} alt={media.altSuffix || s.title} loading="lazy"
                    className={`w-full h-full object-cover ${media.pos || ""} group-hover:scale-105 transition-transform duration-700`} />
                </div>
                <div className="p-9 lg:p-12 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-[var(--ci-grey)]">{s.title}</h3>
                  </div>
                  <p className="text-sm text-[var(--ci-grey)] leading-relaxed mt-4 flex-1">{s.desc}</p>
                  <ul className="mt-8 space-y-2.5">
                    {s.specs.map((spec) => (
                      <li key={spec} className="flex items-center gap-3 font-tech text-[11px] tracking-[0.14em] uppercase text-[var(--ci-grey)]">
                        <HammLogoA data-testid="bullet-logo-a" /> {spec}
                      </li>
                    ))}
                  </ul>
                  <Link to={locale === "en" ? "/en/inquiry-portal" : "/anfrage-portal"} data-testid={`service-cta-${String(i + 1).padStart(2, "0")}`}
                    className="mt-8 self-start flex items-center gap-2 font-tech text-[11px] tracking-[0.2em] uppercase text-[var(--ci-blue)] hover:text-[var(--ci-turquoise)] transition-colors duration-200">
                    {t("services.cta")} <ArrowUpRight size={14} />
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
